import React, { useState, useEffect } from 'react';
import { Save, Loader } from 'lucide-react';
import api, { settingsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import GatewayCredentialFields, { SECTION, FIELD, blankGatewayProfile } from './GatewayFields';

// Settings → Payments → CableTV/Broadband — Online PG, "Two gateways split by amount"
// (2026-07-28). Two merchant accounts for one service, split at a single rupee amount:
// "till ₹1000 through this API, above ₹1000 through that one".
//
// The two sides are ordinary paymentGatewayProfiles — this screen just creates/edits them
// inline and points paymentServiceRouting.<service> at the pair, so there's still one
// credential store, one masking contract, and one router. The alternative (a second pair of
// credential fields hung off the service) would have duplicated all three.
//
// Both sides save together: a half-applied split would quietly route one amount band to a
// profile the other band's save was meant to replace.

const SIDES = [
  { key: 'upTo',  routingKey: 'profileUpTo',  suffix: 'up to' },
  { key: 'above', routingKey: 'profileAbove', suffix: 'above' },
];

// Names are generated rather than asked for — these two profiles exist to serve this split,
// and a stale hand-typed name ("₹1000") after the threshold moves to ₹2000 is worse than no
// name at all. They're what shows in the Gateway Routing tab's profile list.
const profileName = (serviceLabel, suffix, threshold) =>
  `${serviceLabel} — ${suffix} ₹${Number(threshold).toLocaleString('en-IN')}`;

const emptyUi = { showSecret: false, showWebhookSecret: false, showAblepaySalt: false };

export default function AmountSplitGateways({ service, serviceLabel, profiles, routing, onSaved }) {
  const [threshold, setThreshold] = useState(routing.splitThreshold ?? 1000);
  const [drafts, setDrafts] = useState({ upTo: blankGatewayProfile(), above: blankGatewayProfile() });
  const [ui, setUi] = useState({ upTo: emptyUi, above: emptyUi });
  const [saving, setSaving] = useState(false);

  // Which profile _id each side is bound to. The seeding effect keys off this alone, so a
  // `profiles` refresh that doesn't change the binding can't wipe whatever the admin is
  // halfway through typing.
  const boundIds = SIDES.map(s => routing[s.routingKey] || '').join('|');

  useEffect(() => {
    setThreshold(routing.splitThreshold ?? 1000);
    setDrafts(SIDES.reduce((acc, s) => {
      const existing = profiles.find(p => String(p._id) === String(routing[s.routingKey]));
      // Secrets arrive masked; the bullets mean "unchanged" and the server drops them on
      // save, so the API shape can be handed straight to the form.
      acc[s.key] = existing ? { ...blankGatewayProfile(), ...existing } : blankGatewayProfile();
      return acc;
    }, {}));
    setUi({ upTo: emptyUi, above: emptyUi });
  }, [boundIds]); // eslint-disable-line

  const setField = (side, key, val) => setDrafts(d => ({ ...d, [side]: { ...d[side], [key]: val } }));
  const setUiField = (side, key, val) => setUi(u => ({ ...u, [side]: { ...u[side], [key]: val } }));
  const setFeeMethod = (side, method, field, val) => setDrafts(d => ({
    ...d,
    [side]: {
      ...d[side],
      feesByMethod: { ...d[side].feesByMethod, [method]: { ...d[side].feesByMethod[method], [field]: val } },
    },
  }));

  // Enough to know the admin actually filled this side in — the gateway-specific "which
  // fields are mandatory" check is the server's job, this only catches an empty form.
  const looksConfigured = (pg) => (pg.provider === 'ablepay' ? !!pg.ablepayApiKey : !!pg.keyId);

  const save = async () => {
    const amount = Number(threshold);
    if (!Number.isFinite(amount) || amount <= 0) return toast.error('Enter a split amount greater than 0');
    for (const s of SIDES) {
      if (!looksConfigured(drafts[s.key])) {
        return toast.error(`Fill in the API details for payments ${s.suffix} ₹${amount}`);
      }
    }

    setSaving(true);
    try {
      // Persist both profiles first, then repoint the routing — if a credential save fails,
      // the service keeps routing the way it did before rather than at a half-saved pair.
      const ids = {};
      for (const s of SIDES) {
        // isEnabled is left as the admin set it — the routing PUT below refuses a disabled
        // side with a clear message rather than this silently re-enabling it behind them.
        const body = { ...drafts[s.key], name: profileName(serviceLabel, s.suffix, amount) };
        const existingId = routing[s.routingKey];
        const { data } = existingId
          ? await api.put(`/settings/payment-gateway-profiles/${existingId}`, body)
          : await api.post('/settings/payment-gateway-profiles', body);
        ids[s.routingKey] = data.data._id;
      }

      await settingsAPI.saveGatewayChoice(service, {
        mode: 'split',
        splitThreshold: amount,
        profileUpTo: ids.profileUpTo,
        profileAbove: ids.profileAbove,
      });
      toast.success(`${serviceLabel} split saved — ≤ ₹${amount} and above now use separate gateways`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const amountLabel = Number(threshold) > 0 ? `₹${Number(threshold).toLocaleString('en-IN')}` : 'the split amount';

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <SECTION title="Split amount">
            <FIELD label="Split at (₹)" hint={`This amount itself goes through the lower gateway — ${amountLabel} and below use the first API, anything more uses the second.`}>
              <input type="number" min="1" step="1" className="form-control" style={{ maxWidth: 180 }}
                value={threshold} onChange={e => setThreshold(e.target.value)} />
            </FIELD>
          </SECTION>
        </div>
      </div>

      {SIDES.map(s => (
        <div className="card" key={s.key} style={{ marginBottom: 16 }}>
          <div className="card-body">
            <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>
              {s.key === 'upTo' ? `Payments up to ${amountLabel}` : `Payments above ${amountLabel}`}
            </h3>
            <p style={{ fontSize: '.83rem', color: 'var(--text-muted)', margin: '0 0 20px' }}>
              API details for the merchant account that should take {serviceLabel} payments
              {s.key === 'upTo' ? ` of ${amountLabel} or less.` : ` over ${amountLabel}.`}
              {routing[s.routingKey] ? '' : ' Saving creates a gateway profile for it.'}
            </p>
            <GatewayCredentialFields
              pg={drafts[s.key]}
              ui={ui[s.key]}
              setField={(k, v) => setField(s.key, k, v)}
              setUiField={(k, v) => setUiField(s.key, k, v)}
              setFeeMethod={(m, f, v) => setFeeMethod(s.key, m, f, v)}
            />
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving
            ? <><Loader size={14} style={{ marginRight: 6, animation: 'spin 1s linear infinite' }} />Saving…</>
            : <><Save size={14} style={{ marginRight: 6 }} />Save Both Gateways</>}
        </button>
      </div>
    </>
  );
}
